import { Fragment, useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { cn } from "@/lib/utils";

type RenderItem<T> = (item: T, index: number) => React.ReactNode;
type MarqueeCssVariables = React.CSSProperties & {
  "--marquee-distance": string;
  "--marquee-duration": string;
};

interface MarqueeProps<T> {
  items: readonly T[];
  renderItem: RenderItem<T>;
  /** Seconds per loop (one full item-set width). */
  speedSeconds?: number;
  className?: string;
  trackClassName?: string;
  pauseOnHover?: boolean;
}

/**
 * Seamless right-to-left marquee that measures the first item-set width
 * and scrolls exactly that distance to avoid gaps/hitches.
 */
export function Marquee<T>({
  items,
  renderItem,
  speedSeconds = 20,
  className,
  trackClassName,
  pauseOnHover = true,
}: MarqueeProps<T>) {
  const containerRef = useRef<HTMLDivElement>(null);
  const setRef = useRef<HTMLDivElement>(null);

  const [copies, setCopies] = useState(2);
  const [distancePx, setDistancePx] = useState(0);
  const [reducedMotion, setReducedMotion] = useState(false);

  useEffect(() => {
    const mq = window.matchMedia("(prefers-reduced-motion: reduce)");
    const update = () => setReducedMotion(mq.matches);

    update();
    mq.addEventListener?.("change", update);
    return () => mq.removeEventListener?.("change", update);
  }, []);

  useLayoutEffect(() => {
    const measure = () => {
      const containerW = containerRef.current?.clientWidth ?? 0;
      const setW = setRef.current?.scrollWidth ?? 0;
      if (!containerW || !setW) return;

      setDistancePx(setW);

      // Ensure enough copies to cover the viewport so we never see an empty gap.
      const neededCopies = Math.ceil((containerW * 2) / setW) + 1;
      setCopies(Math.max(2, neededCopies));
    };

    measure();

    const ro = new ResizeObserver(() => measure());
    if (containerRef.current) ro.observe(containerRef.current);
    if (setRef.current) ro.observe(setRef.current);

    return () => ro.disconnect();
  }, [items.length]);

  const style = useMemo<MarqueeCssVariables>(() => {
    return {
      "--marquee-distance": `${distancePx}px`,
      "--marquee-duration": `${speedSeconds}s`,
    };
  }, [distancePx, speedSeconds]);

  const shouldAnimate = !reducedMotion && distancePx > 0;

  return (
    <div ref={containerRef} className={cn("relative overflow-hidden", className)} style={style}>
      <style>{`@keyframes lovable-marquee-left{from{transform:translateX(0)}to{transform:translateX(calc(-1 * var(--marquee-distance)))}}`}</style>

      <div
        className={cn(
          "flex w-max whitespace-nowrap",
          pauseOnHover && "hover:[animation-play-state:paused]",
          trackClassName
        )}
        style={
          shouldAnimate
            ? { animation: "lovable-marquee-left var(--marquee-duration) linear infinite" }
            : undefined
        }
      >
        <div ref={setRef} className="flex w-max">
          {items.map((item, index) => (
            <Fragment key={`set-0-${index}`}>{renderItem(item, index)}</Fragment>
          ))}
        </div>

        {Array.from({ length: copies - 1 }).map((_, copyIndex) => (
          <div key={`set-${copyIndex + 1}`} aria-hidden className="flex w-max">
            {items.map((item, index) => (
              <Fragment key={`set-${copyIndex + 1}-${index}`}>{renderItem(item, index)}</Fragment>
            ))}
          </div>
        ))}
      </div>
    </div>
  );
}
